import { get, findIndex, isObject, omit, some } from 'lodash';
import arrayMove from 'array-move/index';
import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_SLIDER,
  QUESTION_RATING,
  QUESTION_RANKING,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_NPS,
  QUESTION_VAN_WESTENDORP,
  GROUP_BASIC,
  GROUP_LOOP_MERGE,
  GROUP_AB_TEST,
  GROUP_CONJOINT,
  GROUP_VAN_WESTENDORP,
} from 'data/nodeTypes';
import { EDIT_PERMISSIONS } from 'containers/Permissions/constants';
import { getOptionType } from 'containers/QuestionnairePage/MatrixRowAndColumnsReOrder/utils';
import { formatQuestionChildLabelsAfterMove } from 'containers/QuestionnairePage/selectorUtils';
import { getCarriedForwardedOptionsByRefId } from 'containers/QuestionnairePage/carriedForwardCopies';
import {
  QA_ACTIONS,
  QA_SUPPORTED_ACTIONS,
  DROPDOWN_TRIGGER_SIZE,
  getIndicatorAreaWidth,
  INDICATORS_TO_DROPDOWN_GAP,
} from './QuestionAnswerDropdownActions/constants';

import {
  defaultAnswersSelector,
  emptyAnswersSelector,
} from './SingleMultipleQuestion/utils';

const hasAnswerDisplayLogic = (answer, answersWithLogicFromOutcomes) =>
  answersWithLogicFromOutcomes.includes(answer.answerRefId || answer.refId);

export const canDragAnswer = (answer, answersWithDisabledActions) => {
  if (answer.isEmptyAnswer) {
    return false;
  }

  if (
    answersWithDisabledActions &&
    answersWithDisabledActions.includes(answer.refId)
  ) {
    return false;
  }

  return true;
};

export const isAnswerDragEnabled = (
  answers,
  emptyAnswers,
  answersWithDisabledActions,
) => {
  const draggableCount = answers.filter((answer) =>
    canDragAnswer(answer, answersWithDisabledActions),
  ).length;

  return draggableCount >= 1;
};

export const moveAnswerByDirection = ({
  answers,
  refId,
  direction,
  answersWithDisabledActions,
}) => {
  const defaultAnswers = defaultAnswersSelector(answers);
  const emptyAnswers = emptyAnswersSelector(answers);
  const sourceIndex = findIndex(defaultAnswers, { refId });
  const targetIndex = sourceIndex + direction;

  if (sourceIndex === -1 || targetIndex === sourceIndex) {
    return null;
  }

  if (targetIndex < 0 || targetIndex >= defaultAnswers.length) {
    return null;
  }

  if (answersWithDisabledActions?.length) {
    const disabledCount = answersWithDisabledActions.length;

    if (targetIndex < disabledCount) {
      return null;
    }

    if (answersWithDisabledActions.includes(refId)) {
      return null;
    }
  }

  const lastActiveIndex = defaultAnswers.length - 1 - emptyAnswers.length;

  if (targetIndex > lastActiveIndex) {
    return null;
  }

  return arrayMove(defaultAnswers, sourceIndex, targetIndex);
};

export const applyDefaultAnswerMoveWithLabels = ({
  question,
  answers,
  refId,
  direction,
  answersWithDisabledActions,
}) => {
  const defaultAnswers = defaultAnswersSelector(answers);
  const answerIndex = findIndex(defaultAnswers, { refId });
  const updatedDefaultAnswers = moveAnswerByDirection({
    answers,
    refId,
    direction,
    answersWithDisabledActions,
  });

  if (!updatedDefaultAnswers) {
    return null;
  }

  return formatQuestionChildLabelsAfterMove(
    { ...question, answers: updatedDefaultAnswers },
    {
      stateKey: 'answers',
      indexA: answerIndex,
      indexB: answerIndex + direction,
      nodeType: question.type,
    },
  ).answers;
};

export const getQuestionPlaceholderHeight = (
  { type, scorepoints = [], answers = [], slider = {}, isCollapsed },
  subNodes,
) => {
  const baseHeight = 56;
  const answersContainerMargin = 8;
  const answersHeight = 32;
  const subNodeContainerMargin = 16;
  const monadicBlockMinHeight = 211;
  const vanWestendorpBlockMinHeight = 211;
  let bodyHeight = 0;
  let logicHeight = 0;
  const hasLogic = true;
  if (hasLogic) {
    logicHeight = 31;
  }
  switch (type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_RANKING: {
      const defaultAnswers = defaultAnswersSelector(answers) || [];
      // we add the expected empty answer
      bodyHeight =
        answersHeight * (defaultAnswers.length + 1) + answersContainerMargin;
      break;
    }
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      // we add the expected empty answer for each row
      bodyHeight = answersHeight * (answers.length + scorepoints.length + 2);
      const rowLabelHeight = 32;
      const columnLabelHeight = 32;
      bodyHeight =
        bodyHeight +
        answersContainerMargin +
        rowLabelHeight +
        columnLabelHeight;
      break;
    }

    case QUESTION_MATRIX_BIPOLAR: {
      // TODO: RES-5957
      break;
    }
    case QUESTION_SLIDER: {
      const ticks = get(slider, 'ticks', []);
      // we add the expected empty answer for each tick
      bodyHeight = answersHeight * (ticks.length + 1) + answersContainerMargin;
      break;
    }
    case GROUP_BASIC:
    case GROUP_LOOP_MERGE:
    case GROUP_CONJOINT:
    case GROUP_VAN_WESTENDORP:
    case GROUP_AB_TEST: {
      bodyHeight = subNodes.reduce(
        (accumulator, node) => accumulator + node.placeholderHeight,
        subNodes.length * subNodeContainerMargin,
      );

      if (type === GROUP_AB_TEST) {
        bodyHeight += monadicBlockMinHeight;
      }

      if (type === GROUP_VAN_WESTENDORP) {
        bodyHeight += vanWestendorpBlockMinHeight;
      }

      break;
    }
    case QUESTION_OPEN_ENDED:
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_RATING:
    case QUESTION_NPS:
    case QUESTION_VAN_WESTENDORP:
    default:
      break;
  }

  return isCollapsed ? baseHeight : baseHeight + bodyHeight + logicHeight;
};

export const isAnswerWithLogic = ({ answer, answersWithLogic }) =>
  findIndex(answersWithLogic, (logicAnswer) => {
    // for support of deprecated pf-advanced-logic v0.3.0
    if (isObject(logicAnswer)) {
      if (logicAnswer.value) {
        if (isObject(logicAnswer.value)) {
          return (
            logicAnswer.value.row === answer.refId ||
            logicAnswer.value.column === answer.refId
          );
        }

        return logicAnswer.value === answer.refId;
      }

      return (
        logicAnswer.row === answer.refId || logicAnswer.column === answer.refId
      );
    }
    return logicAnswer === answer.refId;
  }) > -1;

// Whether a carried-forward copy of the item has logic, at any depth of the
// carry forward chain: removing the item removes that logic too.
export const hasCarriedForwardCopyWithLogic = ({
  nodes = [],
  nodeRefId,
  itemRefId,
  answersWithLogic = [],
}) => {
  const [, ...copies] = getCarriedForwardedOptionsByRefId({
    nodes,
    initialObject: { nodeRefId, valueRefId: itemRefId },
  });

  return copies.some(({ valueRefId }) =>
    isAnswerWithLogic({ answer: { refId: valueRefId }, answersWithLogic }),
  );
};

export const isAnswerWithMention = ({
  allQuestionPrompts = [],
  refId,
  subRefId,
}) => {
  const mentionReference = `{{refId:${refId}/subRefId:${subRefId}}}`;
  return Boolean(
    some(
      allQuestionPrompts,
      ({ prompt = '', htmlPrompt = '' }) =>
        prompt.indexOf(mentionReference) > -1 ||
        htmlPrompt.indexOf(mentionReference) > -1,
    ),
  );
};

// Perf optimisation: cleaning question from properties that cause the question to always rerender
export const getCleanedQuestion = (question) =>
  omit(question, ['scrollIntoView', 'ignoreScrollIfInViewport']);

export const calculateDropdownProps = (props) => {
  const {
    canModifyMedia,
    reorderAnswerDownDisabled,
    reorderAnswerUpDisabled,
    permissions,
    uploadMediaPermission = EDIT_PERMISSIONS.ANSWER_EDIT_MEDIA,
  } = props;

  const dropdownProps = {};

  dropdownProps.canUpsertMedia =
    canModifyMedia && permissions[uploadMediaPermission];

  dropdownProps.canReorderAnswerDown =
    !reorderAnswerDownDisabled && permissions[EDIT_PERMISSIONS.ANSWER_REORDER];
  dropdownProps.canReorderAnswerUp =
    !reorderAnswerUpDisabled && permissions[EDIT_PERMISSIONS.ANSWER_REORDER];

  return dropdownProps;
};

export const getMatrixDropdownReorderProps = ({
  hasReorderPermission,
  isFirst,
  isLast,
  onReorder,
  refId,
  isRow,
  isLabel = false,
  reorderDirection = 'vertical',
}) => {
  const optionType = getOptionType({ isRow, isLabel });
  const reorderProps = calculateDropdownProps({
    permissions: {
      [EDIT_PERMISSIONS.ANSWER_REORDER]: hasReorderPermission,
    },
    reorderAnswerUpDisabled: isFirst,
    reorderAnswerDownDisabled: isLast,
  });

  return {
    canReorder: Boolean(onReorder && hasReorderPermission),
    reorderDirection,
    ...reorderProps,
    reorderAnswerUp: () => onReorder?.(refId, optionType, -1),
    reorderAnswerDown: () => onReorder?.(refId, optionType, 1),
  };
};

export const getQuestionPromptSupportedActions = ({
  isHidden,
  canShowDisplayLogic,
}) => {
  if (isHidden) {
    return [QA_ACTIONS.REMOVE_QUESTION];
  }

  return QA_SUPPORTED_ACTIONS.QUESTION_PROMPT.filter((action) => {
    if (
      action === QA_ACTIONS.NAVIGATION_LOGIC ||
      action === QA_ACTIONS.DISPLAY_LOGIC
    ) {
      return canShowDisplayLogic;
    }

    return true;
  });
};

export const calculateAnswerIndicatorCount = ({
  answer,
  canShowDisplayLogic,
  supportsExclusiveAnswerUI,
  canModifyMedia,
  answersWithLogicFromOutcomes = [],
}) => {
  let count = 0;
  if (
    canShowDisplayLogic &&
    hasAnswerDisplayLogic(answer, answersWithLogicFromOutcomes)
  ) {
    count += 1;
  }
  if (supportsExclusiveAnswerUI && answer.isExclusive) {
    count += 1;
  }
  if (canModifyMedia && answer.media?.url && !answer.isCarriedForward) {
    count += 1;
  }
  return count;
};

export const calculateMaxAnswerIndicatorCount = ({
  answers = [],
  canShowDisplayLogic,
  supportsExclusiveAnswerUI,
  canModifyMedia,
  excludeTypes = [],
  answersWithLogicFromOutcomes = [],
}) =>
  answers.reduce((max, answer) => {
    if (answer.isEmptyAnswer || excludeTypes.includes(answer.type)) {
      return max;
    }
    const count = calculateAnswerIndicatorCount({
      answer,
      canShowDisplayLogic,
      supportsExclusiveAnswerUI,
      canModifyMedia,
      answersWithLogicFromOutcomes,
    });
    return Math.max(max, count);
  }, 0);

const MATRIX_ROW_ACTIONS_CELL_HORIZONTAL_PADDING = 16;
const MATRIX_COLUMN_ACTIONS_GROUP_MIN_WIDTH = DROPDOWN_TRIGGER_SIZE;
const MATRIX_ACTIONS_GROUP_GAP = 2;
const MATRIX_ACTIONS_REMOVE_WIDTH = 14;
const MATRIX_ACTIONS_MIN_WIDTH = 34;

export const calculateMatrixActionsColumnWidth = ({
  maxIndicatorSlots = 0,
  includeRemoveButton = false,
  minWidth = MATRIX_ACTIONS_MIN_WIDTH,
}) => {
  const indicatorsWidth = getIndicatorAreaWidth(maxIndicatorSlots);
  const indicatorsToDropdownGap =
    indicatorsWidth > 0 ? INDICATORS_TO_DROPDOWN_GAP : 0;
  const removeWidth = includeRemoveButton
    ? MATRIX_ACTIONS_GROUP_GAP + MATRIX_ACTIONS_REMOVE_WIDTH
    : 0;

  const width =
    MATRIX_ROW_ACTIONS_CELL_HORIZONTAL_PADDING +
    indicatorsWidth +
    indicatorsToDropdownGap +
    DROPDOWN_TRIGGER_SIZE +
    removeWidth;

  return Math.max(minWidth, width);
};

export const calculateMatrixColumnActionsGroupWidth = ({
  column,
  canShowDisplayLogic = true,
  supportsExclusiveAnswerUI = false,
  includeRemoveButton = false,
  answersWithLogicFromOutcomes = [],
  minWidth = MATRIX_COLUMN_ACTIONS_GROUP_MIN_WIDTH,
}) => {
  const indicatorSlots = calculateAnswerIndicatorCount({
    answer: column,
    canShowDisplayLogic,
    supportsExclusiveAnswerUI,
    canModifyMedia: false,
    answersWithLogicFromOutcomes,
  });
  const indicatorsWidth = getIndicatorAreaWidth(indicatorSlots);
  const indicatorsToDropdownGap =
    indicatorsWidth > 0 ? INDICATORS_TO_DROPDOWN_GAP : 0;
  const removeWidth = includeRemoveButton
    ? MATRIX_ACTIONS_GROUP_GAP + MATRIX_ACTIONS_REMOVE_WIDTH
    : 0;

  const width =
    indicatorsWidth +
    indicatorsToDropdownGap +
    DROPDOWN_TRIGGER_SIZE +
    removeWidth;

  return Math.max(minWidth, width);
};

export const calculateMaxMatrixRowIndicatorSlotCount = ({
  rows = [],
  canShowDisplayLogic = true,
  canModifyMedia = false,
  getRowMedia = () => null,
  answersWithLogicFromOutcomes = [],
}) => {
  const answers = rows
    .filter((row) => row.type !== 'CONSTANT_SUM')
    .map((row) => {
      const rowMedia = getRowMedia(row);
      const hasMedia = Boolean(rowMedia?.url || rowMedia?.media?.url);
      const mediaValue = rowMedia?.media || rowMedia;

      return hasMedia && mediaValue ? { ...row, media: mediaValue } : row;
    });

  return calculateMaxAnswerIndicatorCount({
    answers,
    canShowDisplayLogic,
    supportsExclusiveAnswerUI: false,
    canModifyMedia,
    answersWithLogicFromOutcomes,
  });
};

export const calculateMatrixRowActionsColumnWidth = ({
  rows = [],
  canShowDisplayLogic = true,
  canModifyMedia = false,
  getRowMedia = () => null,
  includeRemoveButton = false,
  answersWithLogicFromOutcomes = [],
  minWidth = MATRIX_ACTIONS_MIN_WIDTH,
}) =>
  rows.reduce((maxWidth, row) => {
    if (row.type === 'CONSTANT_SUM') {
      return maxWidth;
    }

    const rowMedia = getRowMedia(row);
    const hasMedia = Boolean(rowMedia?.url || rowMedia?.media?.url);
    const mediaValue = rowMedia?.media || rowMedia;
    const answerWithMedia =
      hasMedia && mediaValue ? { ...row, media: mediaValue } : row;
    const indicatorSlots = calculateAnswerIndicatorCount({
      answer: answerWithMedia,
      canShowDisplayLogic,
      supportsExclusiveAnswerUI: false,
      canModifyMedia,
      answersWithLogicFromOutcomes,
    });

    return Math.max(
      maxWidth,
      calculateMatrixActionsColumnWidth({
        maxIndicatorSlots: indicatorSlots,
        includeRemoveButton,
        minWidth,
      }),
    );
  }, minWidth);
